import { useCallback } from 'react';

import { useParams } from 'react-router';

import { useCurrentDashboardConfig } from '@/features/dashboard/hooks/useCurrentDashboardConfig';
import { useInitialDashboardName } from '@/features/dashboard/hooks/useInitialDashboardName';
import {
  getEffectiveTabName,
  getTabContent,
  normalizeTabEntry
} from '@/features/dashboard/lib/transformers';
import { useWidgetAnimation } from '@/features/dashboard/state/WidgetAnimation/hooks';
import type { NestedDashboardStructure, MarketApp, MarketApps } from '@/features/dashboard/types';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';
import { buildUpdatedTabContent, getWidgetAnimationIds } from './useBuildDashboardStructure.utils';

export const useBuildDashboardStructure = () => {
  const { setElementsToAnimateId } = useWidgetAnimation();
  const { tabName: rawTabName } = useParams<{ tabName?: string }>();
  const initialDashboardName = useInitialDashboardName();
  const allDashboardTabs = useCurrentDashboardConfig() ?? {};
  const currentTabName = rawTabName ? decodeURIComponent(rawTabName) : undefined;

  return useCallback(
    (widget: MarketApp) => {
      const updatedTabs: Record<string, { id: string; content: MarketApps }> = {};

      const effectiveTabName = getEffectiveTabName(currentTabName);
      const widgetAnimationIds = getWidgetAnimationIds(widget);
      const tabNames = new Set(Object.keys(allDashboardTabs));

      for (const [tabName, tabValue] of Object.entries(allDashboardTabs)) {
        const normalized = normalizeTabEntry(tabName, tabValue);
        if (tabName === effectiveTabName) {
          const updatedContent = buildUpdatedTabContent(getTabContent(tabValue), widget);
          setElementsToAnimateId(widgetAnimationIds);
          updatedTabs[tabName] = { id: normalized.id, content: updatedContent };
        } else {
          updatedTabs[tabName] = { id: normalized.id, content: getTabContent(tabValue) };
        }
      }

      if (!tabNames.has(effectiveTabName)) {
        setElementsToAnimateId(widgetAnimationIds);
        updatedTabs[effectiveTabName] = {
          id: generateUniqueUUID(new Set()),
          content: [widget] as MarketApps
        };
      }

      return { [initialDashboardName]: updatedTabs } as NestedDashboardStructure;
    },
    [allDashboardTabs, currentTabName, initialDashboardName, setElementsToAnimateId]
  );
};
